import { Editor } from '@tiptap/vue-3'
import BaseKit from '@/tiptap/extensions/base-kit'
import GlobalIds from '@/tiptap/extensions/global-ids'
import History from '@/tiptap/extensions/history'
import Blockquote from '@/tiptap/extensions/blockquote'
import HorizontalRule from '@/tiptap/extensions/horizontal-rule'
import Table from '@/tiptap/extensions/table'
import { createMentionExtension } from '../extensions/mentions'
import Bold from '@/tiptap/extensions/bold.js'
import Italic from '@/tiptap/extensions/italic.js'
import Underline from '@/tiptap/extensions/underline.js'
import BulletList from '@/tiptap/extensions/bullet-list'
import OrderedList from '@/tiptap/extensions/ordered-list'
import TaskList from '@/tiptap/extensions/task-list'
import Indent from '@/tiptap/extensions/indent'
import Link from '@/tiptap/extensions/link'
import { Image } from '@/tiptap/extensions/image'
import Video from '@/tiptap/extensions/video'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@/tiptap/extensions/color'
import Highlight from '@/tiptap/extensions/highlight'
import Strike from '@/tiptap/extensions/strike'
import Heading from '@/tiptap/extensions/heading'
import FontFamily from '@/tiptap/extensions/font-family'
import FontSize from '@/tiptap/extensions/font-size'
import FontWeight from '@/tiptap/extensions/font-weight'
import LetterSpacing from '@/tiptap/extensions/letter-spacing'
import TextAlignMenu from '@/tiptap/extensions/text-align'
import Clear from '@/tiptap/extensions/clear'
import SubAndSuperScript from '@/tiptap/extensions/subAndSuperScript'

import { tiptapUploadImage as tiptapImageUploader } from '@/utils/tiptapImageUploader'

export function createEditor({ content = '<p></p>', features = {} } = {}) {
  const f = {
    tables: false,
    images: false,
    video: true,
    links: true,
    codeBlock: true,
    quote: true,
    color: true,
    highlight: true,
    strike: true,
    bulletList: true,
    orderedList: true,
    taskList: true,
    indent: true,
    horizontalRule: true,
    mentions: false,        // For handbook (#)
    userMentions: false,    // For forum (@)
    heading: true,
    fontFamily: true,
    fontSize: true,
    fontWeight: true,
    letterSpacing: true,
    textAlign: true,
    clear: true,
    subSuper: true,
    ...features,
  }

  const rawExtensions = [
    // base + undo/redo core
    BaseKit,
    GlobalIds,
    History.configure({ sort: 240 }),

    // text formatting
    Bold.configure({ sort: 10 }),
    Italic.configure({ sort: 20 }),
    Underline.configure({ sort: 30 }),
    Strike.configure({ sort: 40, divider: true }),

    // heading and alignment
    ...(f.heading ? [Heading.configure({ sort: 50 })] : []),
    ...(f.textAlign ? [TextAlignMenu.configure({ sort: 60, types: ['heading', 'paragraph'] })] : []),

    // fonts and colors
    ...(f.fontFamily ? [FontFamily.configure({ sort: 70 })] : []),
    ...(f.fontSize ? [FontSize.configure({ sort: 80 })] : []),
    ...(f.fontWeight ? [FontWeight.configure({ sort: 10 })] : []),
    ...(f.letterSpacing ? [LetterSpacing.configure({ sort: 87, divider: true })] : []),
    TextStyle, // plain
    ...(f.color ? [Color.configure({ sort: 90 })] : []),
    ...(f.highlight ? [Highlight.configure({ sort: 100, divider: true })] : []),

    // subs/superscript and clear
    ...(f.subSuper ? [SubAndSuperScript.configure({ sort: 110, divider: true })] : []),
    ...(f.clear ? [Clear.configure({ sort: 120, divider: true })] : []),

    // lists
    ...(f.bulletList ? [BulletList.configure({ sort: 130 })] : []),
    ...(f.orderedList ? [OrderedList.configure({ sort: 140 })] : []),
    ...(f.taskList ? [TaskList.configure({ sort: 150 })] : []),

    // indent / outdent
    ...(f.indent ? [Indent.configure({ sort: 170, divider: true })] : []),

    // links and media
    ...(f.links ? [
      Link.configure({
        sort: 180,
        autolink: true,
        openOnClick: false,
        linkOnPaste: true,
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }),
    ] : []),

    ...(f.images ? [
      Image.configure({
        sort: 190,
        inline: true,
        allowBase64: false,
        upload: tiptapImageUploader,
        imageTabs: ['upload', 'url'],
        hiddenTabs: [],
      }),
    ] : []),

    ...(f.video ? [Video.configure({ sort: 200 })] : []),
    ...(f.tables ? [
      Table.configure({ sort: 210, resizable: true, HTMLAttributes: { class: 'table-wrapper' }, divider: true }),
    ] : []),

    // quotes & dividers
    Blockquote.configure({ sort: 220 }),
    ...(f.horizontalRule ? [HorizontalRule.configure({ sort: 230, divider: true })] : []),

    // mentions (don’t appear in toolbar)
    ...(f.mentions ? [createMentionExtension({
      char: '#',
      className: 'mention-handbook',
      apiPath: '/api/handbook-sections/suggestions/',
      hrefBuilder: (item) => `/handbook/article/${item.id}`,
      mapItem: (raw) => ({ id: raw.id, label: raw.title }),
    })] : []),

    ...(f.userMentions ? [createMentionExtension({
      char: '@',
      className: 'mention-user',
      apiPath: '/api/forum/users/suggestions/',
      hrefBuilder: (item) => `/profile/${item.id}`,
      mapItem: (raw) => ({ id: raw.id, label: raw.username }),
    })] : []),
  ]

  const seen = new Set()
  const extensions = rawExtensions.filter(ext => {
    const name = ext?.name || ext?.config?.name
    if (!name) return true
    if (seen.has(name)) return false
    seen.add(name)
    return true
  })

  return new Editor({
    editorProps: {
      attributes: {
        class: 'tiptap',
      },
      handleClick: () => false,
    },
    extensions,
    content,
  })
}
